import { IBusinessEntity, IEntitlement } from '@commenda/types';
import {
  Country,
  EntitlementEntityType,
  ObligationRulesCategory,
} from '@prisma/client';

export enum CompliancePlanIdentifier {
  COMPLIANCE_BASIC_IN = 'COMPLIANCE_BASIC_IN',
  COMPLIANCE_BASIC_SG = 'COMPLIANCE_BASIC_SG',
  COMPLIANCE_BASIC_UK = 'COMPLIANCE_BASIC_UK',
  COMPLIANCE_BASIC_US = 'COMPLIANCE_BASIC_US',
  COMPLIANCE_BASIC_CA = 'COMPLIANCE_BASIC_CA',
  COMPLIANCE_BASIC_IE = 'COMPLIANCE_BASIC_IE',

  // // Compliance pro plans
  COMPLIANCE_PRO_IN = 'COMPLIANCE_PRO_IN',
  COMPLIANCE_PRO_SG = 'COMPLIANCE_PRO_SG',
  COMPLIANCE_PRO_UK = 'COMPLIANCE_PRO_UK',
  COMPLIANCE_PRO_US = 'COMPLIANCE_PRO_US',
  COMPLIANCE_PRO_CA = 'COMPLIANCE_PRO_CA',
}

/*
To find if a compliance calendar is supported by Commenda we check isComplianceLaunched feature. 
*/
export class ComplianceModel {
  static hasAllFieldsForComplianceCalenderGeneration(
    businessEntity: IBusinessEntity
  ) {
    return (
      !!businessEntity.generalCorporationInfo.formationDate &&
      !!businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE?.day &&
      !!businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE?.month
    );
  }

  // used for payment (choosing file with commenda) in compliance onboarding
  // compliance calendar itself is feature flagged in ComplianceLaunchStages in launch-stages.ts
  static isComplianceByCommendaSupported(country: Country) {
    const supportedCountries: Country[] = [
      Country.US,
      Country.GB,
      Country.SG,
      Country.IN,
    ];
    return supportedCountries.includes(country);
  }

  static getFormattedComplianceCategory(
    category: ObligationRulesCategory,
    _country?: Country
  ): string {
    const defaultMappings: Partial<Record<ObligationRulesCategory, string>> = {
      [ObligationRulesCategory.DIRECT_TAX]: 'Direct Tax',
      [ObligationRulesCategory.INDIRECT_TAX]: 'Indirect Tax',
      [ObligationRulesCategory.TRANSFER_PRICING]: 'Transfer Pricing',
      [ObligationRulesCategory.ENTITY_COMPLIANCE]: 'Entity Compliance',
      [ObligationRulesCategory.PAYROLL_AND_EMPLOYMENT]:
        'Payroll and Employment',
      [ObligationRulesCategory.CUSTOMS_AND_TRADE]: 'Customs and Trade',
    };

    if (defaultMappings[category]) return defaultMappings[category]!;

    // Fallback for legacy categories while old values still exist in the DB enum.
    return category
      .split('_')
      .map((word) => word[0] + word.slice(1).toLowerCase())
      .join(' ');
  }

  static getCompliancePlansIdentifier(country: Country) {
    const compliancePlanMapping: Partial<
      Record<Country, CompliancePlanIdentifier[]>
    > = {
      [Country.IN]: [
        CompliancePlanIdentifier.COMPLIANCE_BASIC_IN,
        CompliancePlanIdentifier.COMPLIANCE_PRO_IN,
      ],
      [Country.SG]: [
        CompliancePlanIdentifier.COMPLIANCE_BASIC_SG,
        CompliancePlanIdentifier.COMPLIANCE_PRO_SG,
      ],
      [Country.GB]: [
        CompliancePlanIdentifier.COMPLIANCE_BASIC_UK,
        CompliancePlanIdentifier.COMPLIANCE_PRO_UK,
      ],
      [Country.US]: [
        CompliancePlanIdentifier.COMPLIANCE_BASIC_US,
        CompliancePlanIdentifier.COMPLIANCE_PRO_US,
      ],
      [Country.CA]: [
        CompliancePlanIdentifier.COMPLIANCE_BASIC_CA,
        CompliancePlanIdentifier.COMPLIANCE_PRO_CA,
      ],
      [Country.IE]: [CompliancePlanIdentifier.COMPLIANCE_BASIC_IE],
    };

    return compliancePlanMapping[country] || [];
  }

  /**
   * Compliance plans the company owns but has not attached to any entity yet.
   * These are what the onboarding wizard offers up for consumption.
   */
  static getUnclaimedCompliancePlans(
    entitlements: IEntitlement[] | undefined,
    country: Country
  ) {
    const compliancePlans =
      ComplianceModel.getCompliancePlansIdentifier(country);

    return (entitlements || []).filter(
      (ent) =>
        ent.forEntityType === EntitlementEntityType.BUSINESS_ENTITY &&
        !ent.forEntityId &&
        compliancePlans.includes(
          ent.productIdentifier as CompliancePlanIdentifier
        )
    );
  }

  /** True once a compliance plan is already attached to this specific entity. */
  static hasClaimedCompliancePlan(
    entitlements: IEntitlement[] | undefined,
    businessEntityId: number,
    country: Country
  ) {
    const compliancePlans =
      ComplianceModel.getCompliancePlansIdentifier(country);

    return (entitlements || []).some(
      (ent) =>
        ent.forEntityType === EntitlementEntityType.BUSINESS_ENTITY &&
        ent.forEntityId === String(businessEntityId) &&
        compliancePlans.includes(
          ent.productIdentifier as CompliancePlanIdentifier
        )
    );
  }
}
